import { useEffect, useMemo, useState } from "react";
import { Download, ChevronLeft, ChevronRight, Fingerprint, ShieldCheck, UserSquare2, Building2 } from "lucide-react";
import AppShell from "../AppShell";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { PageHeader, Button, ErrorBanner, StatusPill, Kicker } from "../../components/primitives";
import ComplianceDossier, { DOSSIER_PAGE_PX } from "../../components/report/ComplianceDossier";
import { exportDossierPDF } from "../../components/report/exportDossierPDF";

/**
 * Reports — inspection-grade compliance dossier.
 *
 * The Reports page previews the exact same <ComplianceDossier /> component
 * that is rendered offscreen by exportDossierPDF, so on-screen preview
 * and downloaded PDF are pixel-identical.
 *
 * Two modes:
 *   • Full workspace dossier (default) — cover, staff matrix,
 *     per-employee evidence, doc register, cert register, timeline,
 *     exceptions, integrity.
 *   • Individual compliance record — focused on one employee. Suitable
 *     for a starter/leaver HR file: cover (employee-scoped), their
 *     evidence page, their certificate register, their timeline, their
 *     exceptions, integrity.
 */
export default function Reports() {
  const { business } = useAuth();
  const [state, setState] = useState({
    summary: null,
    team: [],
    docs: [],
    certs: [],
    assignments: [],
    competencyAssignments: [],
    loaded: false,
  });
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [zoom, setZoom] = useState(0.72);
  const [focusEmployeeId, setFocusEmployeeId] = useState(""); // "" = full workspace

  useEffect(() => {
    Promise.all([
      api.get("/compliance/summary"),
      api.get("/team"),
      api.get("/documents"),
      api.get("/certificates"),
      api.get("/assignments"),
      api.get("/competencies/assignments").catch(() => []),
      api.get("/business").catch(() => ({})),
    ])
      .then(([s, t, d, c, a, ca, biz]) => {
        setState({
          summary: s,
          team: t || [],
          docs: d || [],
          certs: c || [],
          assignments: a || [],
          competencyAssignments: ca || [],
          compliance_settings: biz?.compliance_settings || {},
          loaded: true,
        });
      })
      .catch((e) => setError(e.message));
  }, []);

  // Reset preview to the first page whenever the mode / focus changes so
  // the pager doesn't point at a nonexistent index.
  useEffect(() => setPreviewIndex(0), [focusEmployeeId]);

  const generatedAt = useMemo(() => new Date(), []);
  const reportId = useMemo(
    () => buildReportId(business, generatedAt, focusEmployeeId, state.team),
    [business, generatedAt, focusEmployeeId, state.team]
  );
  const verifyOrigin = useMemo(
    () => (typeof window !== "undefined" ? window.location.origin : ""),
    []
  );

  const focusEmployee = useMemo(
    () => state.team.find((m) => m.id === focusEmployeeId) || null,
    [state.team, focusEmployeeId]
  );

  const dossierProps = {
    business,
    summary: state.summary,
    team: state.team,
    docs: state.docs,
    certs: state.certs,
    assignments: state.assignments,
    competencyAssignments: state.competencyAssignments,
    compliance_settings: state.compliance_settings,
    reportId,
    generatedAt,
    verifyOrigin,
    focusEmployeeId: focusEmployeeId || null,
  };

  async function handleDownload() {
    setDownloading(true);
    setError("");
    try {
      const businessSlug =
        (business?.name || "cannicomply-workspace")
          .replace(/[^a-z0-9]+/gi, "-")
          .toLowerCase()
          .replace(/^-+|-+$/g, "") || "cannicomply-workspace";
      let filename;
      if (focusEmployee) {
        const empSlug =
          (focusEmployee.name || "employee")
            .replace(/[^a-z0-9]+/gi, "-")
            .toLowerCase()
            .replace(/^-+|-+$/g, "") || "employee";
        filename = `${empSlug}-compliance-record.pdf`;
      } else {
        filename = `${businessSlug}-compliance-dossier.pdf`;
      }
      await exportDossierPDF(dossierProps, filename);
    } catch (e) {
      setError("Could not build PDF — " + e.message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <AppShell>
      <PageHeader
        testid="reports-page"
        kicker="Compliance dossier"
        title="Inspection-ready evidence pack"
        description="A dedicated A4 dossier — cover, staff matrix, per-employee evidence, SOP register, certificate register, audit timeline, exceptions and evidence integrity. Toggle to Individual to generate a starter/leaver HR record for a single employee."
        action={
          <div className="flex items-center gap-2">
            <StatusPill tone="emerald" icon={ShieldCheck}>
              Signed · SHA-256
            </StatusPill>
            <Button
              variant="accent"
              icon={Download}
              loading={downloading}
              onClick={handleDownload}
              disabled={downloading || !state.loaded}
              data-testid="reports-print"
            >
              {downloading
                ? "Building dossier…"
                : focusEmployee
                ? "Download record"
                : "Download dossier"}
            </Button>
          </div>
        }
      />

      <ErrorBanner error={error} className="mb-5" />

      {!state.loaded && (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500 text-[13px]">
          Preparing your dossier…
        </div>
      )}

      {state.loaded && (
        <>
          <ModeSelector
            focusEmployeeId={focusEmployeeId}
            setFocusEmployeeId={setFocusEmployeeId}
            team={state.team}
          />

          <MetaStrip
            reportId={reportId}
            generatedAt={generatedAt}
            business={business}
            focusEmployee={focusEmployee}
            zoom={zoom}
            setZoom={setZoom}
          />

          <PreviewShell
            dossierProps={dossierProps}
            previewIndex={previewIndex}
            setPreviewIndex={setPreviewIndex}
            zoom={zoom}
          />
        </>
      )}
    </AppShell>
  );
}

/* -------------------- Mode selector (workspace vs individual) -------------------- */
function ModeSelector({ focusEmployeeId, setFocusEmployeeId, team }) {
  const workspaceMode = !focusEmployeeId;
  return (
    <div
      className="mb-4 rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3 flex-wrap"
      data-testid="reports-mode-selector"
    >
      <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8C6B33]">
        Report mode
      </span>
      <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50">
        <button
          type="button"
          onClick={() => setFocusEmployeeId("")}
          data-testid="reports-mode-workspace"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${
            workspaceMode
              ? "bg-white text-slate-900 shadow-sm border border-slate-200"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          <Building2 size={12} />
          Full workspace
        </button>
        <button
          type="button"
          onClick={() => {
            if (!focusEmployeeId && team.length > 0) {
              setFocusEmployeeId(team[0].id);
            }
          }}
          data-testid="reports-mode-individual"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${
            !workspaceMode
              ? "bg-white text-slate-900 shadow-sm border border-slate-200"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          <UserSquare2 size={12} />
          Individual employee
        </button>
      </div>

      {!workspaceMode && (
        <label className="inline-flex items-center gap-2 ml-2">
          <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">
            Employee
          </span>
          <select
            value={focusEmployeeId}
            onChange={(e) => setFocusEmployeeId(e.target.value)}
            data-testid="reports-focus-employee"
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[12.5px] text-slate-900 font-semibold focus:border-[#D4A65A] focus:outline-none focus:ring-2 focus:ring-[#D4A65A]/20"
          >
            {team.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.role ? ` — ${m.role}` : ""}
              </option>
            ))}
          </select>
        </label>
      )}

      <span className="ml-auto text-[11px] text-slate-500">
        {workspaceMode
          ? "Full workspace evidence — every employee, document and certificate."
          : "Single-employee record — suitable for HR files, starters and leavers."}
      </span>
    </div>
  );
}

/* -------------------- Meta strip above the preview -------------------- */
function MetaStrip({ reportId, generatedAt, business, focusEmployee, zoom, setZoom }) {
  return (
    <div className="mb-5 grid md:grid-cols-4 gap-3">
      <MetaTile label="Report ID" value={reportId} mono testid="reports-meta-id" />
      <MetaTile
        label="Generated"
        value={generatedAt.toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
        mono
      />
      <MetaTile
        label={focusEmployee ? "Employee" : "Organisation"}
        value={focusEmployee ? focusEmployee.name : business?.name || "—"}
      />
      <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-2">
        <Kicker>Preview zoom</Kicker>
        <input
          type="range"
          min="0.4"
          max="1"
          step="0.02"
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="flex-1 accent-[#8C6B33]"
          aria-label="Preview zoom"
        />
        <span className="text-[11px] font-mono text-slate-600 tabular-nums">
          {Math.round(zoom * 100)}%
        </span>
      </div>
    </div>
  );
}

function MetaTile({ label, value, mono, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-[9.5px] uppercase tracking-[0.22em] font-semibold text-[#8C6B33] flex items-center gap-1.5">
        <Fingerprint size={10} />
        {label}
      </div>
      <div
        data-testid={testid}
        className={`mt-1 text-[13px] text-slate-900 font-semibold truncate ${mono ? "font-mono" : ""}`}
      >
        {value}
      </div>
    </div>
  );
}

/* -------------------- Preview shell -------------------- */
function PreviewShell({ dossierProps, previewIndex, setPreviewIndex, zoom }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-100 p-4 md:p-6 relative">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-slate-500">
          Live preview
        </div>
        <PagerControls index={previewIndex} setIndex={setPreviewIndex} />
      </div>

      <div className="mx-auto overflow-auto max-h-[80vh] rounded-lg bg-slate-100">
        <div style={{ width: `${DOSSIER_PAGE_PX.width * zoom}px`, margin: "0 auto" }}>
          <div
            style={{
              width: `${DOSSIER_PAGE_PX.width}px`,
              transform: `scale(${zoom})`,
              transformOrigin: "top left",
            }}
          >
            <ScrollingDossier dossierProps={dossierProps} activeIndex={previewIndex} />
          </div>
        </div>
      </div>
    </div>
  );
}

function PagerControls({ index, setIndex }) {
  const [pageCount, setPageCount] = useState(1);
  useEffect(() => {
    const check = () => {
      const n = document.querySelectorAll(
        '[data-testid="compliance-dossier"] [data-dossier-page="1"]'
      ).length;
      if (n > 0) setPageCount(n);
    };
    check();
    const t = setInterval(check, 400);
    return () => clearInterval(t);
  }, []);
  const clamp = (i) => Math.max(0, Math.min(pageCount - 1, i));
  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => setIndex(clamp(index - 1))}
        className="h-8 w-8 rounded-md border border-slate-200 bg-white flex items-center justify-center hover:border-slate-300 disabled:opacity-40"
        disabled={index === 0}
        aria-label="Previous page"
      >
        <ChevronLeft size={14} />
      </button>
      <span className="text-[11.5px] font-mono text-slate-700 tabular-nums">
        {index + 1} / {pageCount}
      </span>
      <button
        type="button"
        onClick={() => setIndex(clamp(index + 1))}
        className="h-8 w-8 rounded-md border border-slate-200 bg-white flex items-center justify-center hover:border-slate-300 disabled:opacity-40"
        disabled={index >= pageCount - 1}
        aria-label="Next page"
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

function ScrollingDossier({ dossierProps, activeIndex }) {
  useEffect(() => {
    const pages = document.querySelectorAll(
      '[data-testid="compliance-dossier"] [data-dossier-page="1"]'
    );
    if (pages[activeIndex]) {
      pages[activeIndex].scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeIndex]);
  return <ComplianceDossier {...dossierProps} />;
}

/* -------------------- Report ID -------------------- */
function buildReportId(business, generatedAt, focusEmployeeId, team) {
  const bizSlug =
    (business?.name || "CC")
      .replace(/[^a-z0-9]+/gi, "")
      .slice(0, 4)
      .toUpperCase() || "CC";
  const yyyymmdd = generatedAt.toISOString().slice(0, 10).replace(/-/g, "");
  const hhmm = generatedAt.toISOString().slice(11, 16).replace(":", "");
  if (focusEmployeeId) {
    const emp = (team || []).find((m) => m.id === focusEmployeeId);
    const empSlug =
      (emp?.name || focusEmployeeId)
        .replace(/[^a-z0-9]+/gi, "")
        .slice(0, 5)
        .toUpperCase() || "EMP";
    return `CC-${bizSlug}-${empSlug}-${yyyymmdd}-${hhmm}`;
  }
  return `CC-${bizSlug}-${yyyymmdd}-${hhmm}`;
}
